/** Explicit format-painter serialization. Ordinary edits never enter these paths. */
import type { PptxShape, PptxTextBody, PptxParagraph, PptxRun, PptxFill } from './pptxParser';
import { pxToEmu } from './pptxParser';
const A = 'http://schemas.openxmlformats.org/drawingml/2006/main';
type SetColor = (doc: Document, element: Element, color?: string) => boolean;
const child = (el: Element, name: string) => Array.from(el.children).find(c => c.namespaceURI === A && c.localName === name);
const remove = (el: Element, names: readonly string[]) => { for (const c of Array.from(el.children)) if (c.namespaceURI === A && names.includes(c.localName)) el.removeChild(c); };
const node = (doc: Document, name: string, attrs: Record<string, string | number> = {}) => {
  const el = doc.createElementNS(A, `a:${name}`); for (const [key, value] of Object.entries(attrs)) el.setAttribute(key, String(value)); return el;
};
const FILL = ['noFill', 'solidFill', 'gradFill', 'blipFill', 'pattFill', 'grpFill'];
function insertBefore(doc: Document, parent: Element, el: Element, after: string[]) {
  const before = Array.from(parent.children).find(c => c.namespaceURI === A && after.includes(c.localName));
  parent.insertBefore(el, before ?? null);
}
function solid(doc: Document, color: string | undefined, setColor: SetColor): Element | null {
  const el = node(doc, 'solidFill'); return setColor(doc, el, color) ? el : null;
}
function fillElement(doc: Document, fill: PptxFill, setColor: SetColor): Element {
  if (fill.type === 'solid') return solid(doc, fill.color, setColor) ?? node(doc, 'noFill');
  if (fill.type === 'gradient' && fill.gradientStops?.length) {
    const el = node(doc, 'gradFill', { rotWithShape: 1 }); const stops = node(doc, 'gsLst');
    for (const stop of fill.gradientStops) {
      const gs = node(doc, 'gs', { pos: Math.round(stop.position * 1000) }); const color = solid(doc, stop.color, setColor);
      if (color?.firstElementChild) gs.appendChild(color.firstElementChild); stops.appendChild(gs);
    }
    el.appendChild(stops);
    if (fill.gradientKind === 'radial') el.appendChild(node(doc, 'path', { path: 'circle' }));
    else el.appendChild(node(doc, 'lin', { ang: Math.round((fill.gradientAngle ?? 0) * 60000), scaled: 1 }));
    return el;
  }
  if (fill.type === 'pattern') {
    const el = node(doc, 'pattFill', { prst: fill.patternPreset ?? 'pct5' });
    for (const [name, color] of [['fgClr', fill.patternFgColor ?? '#000000'], ['bgClr', fill.patternBgColor ?? '#ffffff']] as const) {
      const wrapper = node(doc, name); const sf = solid(doc, color, setColor);
      if (sf?.firstElementChild) wrapper.appendChild(sf.firstElementChild); el.appendChild(wrapper);
    }
    return el;
  }
  return node(doc, 'noFill');
}
export function patchPaintedShape(doc: Document, spPr: Element, shape: PptxShape, setColor: SetColor): void {
  remove(spPr, [...FILL, 'ln']);
  insertBefore(doc, spPr, fillElement(doc, shape.fill ?? { type: 'none' }, setColor), ['effectLst', 'effectDag', 'scene3d', 'sp3d', 'extLst']);
  const outline = shape.outline;
  const ln = node(doc, 'ln', outline ? { w: pxToEmu(outline.width ?? 1), cap: outline.cap ?? 'flat' } : {});
  ln.appendChild(outline ? solid(doc, outline.color ?? '#000000', setColor) ?? node(doc, 'noFill') : node(doc, 'noFill'));
  if (outline) {
    if (outline.customDash?.length && outline.customDash.length % 2 === 0) {
      const dash = node(doc, 'custDash');
      for (let i = 0; i < outline.customDash.length; i += 2) dash.appendChild(node(doc, 'ds', { d: Math.round(outline.customDash[i] * 100000), sp: Math.round(outline.customDash[i + 1] * 100000) }));
      ln.appendChild(dash);
    } else ln.appendChild(node(doc, 'prstDash', { val: outline.dashStyle ?? 'solid' }));
    for (const [name, end] of [['headEnd', outline.headEnd], ['tailEnd', outline.tailEnd]] as const) ln.appendChild(node(doc, name, { type: end?.type ?? 'none', ...(end?.w ? { w: end.w } : {}), ...(end?.len ? { len: end.len } : {}) }));
  }
  insertBefore(doc, spPr, ln, ['effectLst', 'effectDag', 'scene3d', 'sp3d', 'extLst']);
}
export function patchPaintedRun(doc: Document, rPr: Element, run: Partial<PptxRun>, setColor: SetColor): void {
  for (const [name, value] of Object.entries({ b: run.bold ? '1' : '0', i: run.italic ? '1' : '0',
    u: run.underline ? 'sng' : 'none', strike: run.strike ? 'sngStrike' : 'noStrike', baseline: String(run.baseline ?? 0) })) rPr.setAttribute(name, value);
  if (run.fontSize !== undefined) rPr.setAttribute('sz', String(Math.round(run.fontSize * 100)));
  remove(rPr, [...FILL, 'highlight', 'latin', 'ea', 'cs']);
  const color = solid(doc, run.color, setColor);
  if (color) insertBefore(doc, rPr, color, ['effectLst', 'effectDag', 'highlight', 'uLnTx', 'uLn', 'uFillTx', 'uFill', 'latin', 'ea', 'cs', 'sym', 'hlinkClick', 'hlinkMouseOver', 'rtl', 'extLst']);
  if (run.highlight) {
    const highlight = node(doc, 'highlight'); const sf = solid(doc, run.highlight, setColor);
    if (sf?.firstElementChild) highlight.appendChild(sf.firstElementChild);
    insertBefore(doc, rPr, highlight, ['uLnTx', 'uLn', 'uFillTx', 'uFill', 'latin', 'ea', 'cs', 'sym', 'hlinkClick', 'hlinkMouseOver', 'rtl', 'extLst']);
  }
  for (const [name, family] of [['latin', run.fontFamily], ['ea', run.eaFontFamily], ['cs', run.csFontFamily]] as const) if (family) {
    insertBefore(doc, rPr, node(doc, name, { typeface: family }), ['sym', 'hlinkClick', 'hlinkMouseOver', 'rtl', 'extLst']);
  }
  // hlinkClick/hlinkMouseOver and their relationship IDs are intentionally untouched.
}
export function patchPaintedParagraph(doc: Document, pPr: Element, para: PptxParagraph, setColor: SetColor): void {
  pPr.setAttribute('algn', para.alignment ?? 'l'); pPr.setAttribute('lvl', String(para.level ?? 0));
  pPr.setAttribute('marL', String(pxToEmu(para.indent?.left ?? 0)));
  pPr.setAttribute('indent', String(pxToEmu((para.indent?.firstLine ?? 0) - (para.indent?.hanging ?? 0))));
  const names = ['lnSpc', 'spcBef', 'spcAft', 'buClrTx', 'buClr', 'buSzTx', 'buSzPct', 'buSzPts', 'buFontTx', 'buFont', 'buNone', 'buAutoNum', 'buChar', 'buBlip', 'defRPr'];
  remove(pPr, names);
  const nodes: Element[] = [];
  const spacing = para.spacing?.lineSpacing ?? 100;
  const ln = node(doc, 'lnSpc'); ln.appendChild(node(doc, spacing < 0 ? 'spcPts' : 'spcPct', { val: Math.round(spacing < 0 ? -spacing * 100 : spacing * 1000) })); nodes.push(ln);
  for (const [name, value] of [['spcBef', para.spacing?.before ?? 0], ['spcAft', para.spacing?.after ?? 0]] as const) {
    const el = node(doc, name); el.appendChild(node(doc, 'spcPts', { val: Math.round(value * 100) })); nodes.push(el);
  }
  if (para.bulletColor) {
    const color = node(doc, 'buClr'); const sf = solid(doc, para.bulletColor, setColor); if (sf?.firstElementChild) color.appendChild(sf.firstElementChild); nodes.push(color);
  } else nodes.push(node(doc, 'buClrTx'));
  nodes.push(para.bulletSizePts !== undefined ? node(doc, 'buSzPts', { val: Math.round(para.bulletSizePts * 100) })
    : para.bulletSizePct !== undefined ? node(doc, 'buSzPct', { val: Math.round(para.bulletSizePct * 100000) }) : node(doc, 'buSzTx'));
  nodes.push(para.bulletFontFamily ? node(doc, 'buFont', { typeface: para.bulletFontFamily }) : node(doc, 'buFontTx'));
  nodes.push(para.bulletType === 'char' ? node(doc, 'buChar', { char: para.bulletChar ?? '•' })
    : para.bulletType === 'autoNum' ? node(doc, 'buAutoNum', { type: para.bulletAutoNumType ?? 'arabicPeriod' }) : node(doc, 'buNone'));
  for (const el of nodes) insertBefore(doc, pPr, el, ['tabLst', 'extLst']);
  const defaults = node(doc, 'defRPr'); patchPaintedRun(doc, defaults, para.defaultRunProps ?? {}, setColor);
  insertBefore(doc, pPr, defaults, ['extLst']);
}
export function patchPaintedBody(doc: Document, txBody: Element, body: PptxTextBody): void {
  let props = child(txBody, 'bodyPr'); if (!props) { props = node(doc, 'bodyPr'); txBody.insertBefore(props, txBody.firstChild); }
  const bp = body.bodyProps;
  for (const key of ['anchor', 'wrap', 'horzOverflow', 'vertOverflow'] as const) if (bp[key] !== undefined) props.setAttribute(key, bp[key]!); else props.removeAttribute(key);
  props.setAttribute('anchorCtr', bp.anchorCtr ? '1' : '0');
  for (const key of ['lIns', 'rIns', 'tIns', 'bIns'] as const) if (bp[key] !== undefined) props.setAttribute(key, String(pxToEmu(bp[key]!))); else props.removeAttribute(key);
  remove(props, ['noAutofit', 'normAutofit', 'spAutoFit']);
  const autofit = node(doc, bp.autofit === 'shrink' ? 'normAutofit' : bp.autofit === 'normal' ? 'spAutoFit' : 'noAutofit');
  if (bp.autofit === 'shrink') {
    autofit.setAttribute('fontScale', String(Math.round((bp.autofitFontScale ?? 1) * 100000)));
    autofit.setAttribute('lnSpcReduction', String(Math.round((bp.autofitLnSpcReduction ?? 0) * 100000)));
  }
  insertBefore(doc, props, autofit, ['scene3d', 'sp3d', 'flatTx', 'extLst']);
}
